import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import AppShell from "@/components/AppShell";
import { STATUS_META, formatSqYd } from "@/lib/statusUtils";
import { formatDate } from "@/lib/statusUtils";
import { Map, Users, BookOpenCheck, TrendingUp, ArrowRight, ShieldAlert, AlertTriangle, Flame, Timer } from "lucide-react";

export default function Dashboard() {
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [lps, setLps] = useState([]);
  const [suspicious, setSuspicious] = useState(null);
  const [adminLoad, setAdminLoad] = useState(null);

  const isSuper = user?.role === "super_admin";

  useEffect(() => {
    (async () => {
      try {
        const [s, l] = await Promise.all([api.get("/dashboard/stats"), api.get("/lps")]);
        setStats(s.data);
        setLps(l.data);
      } catch (e) {
        console.error(e);
      }
    })();
  }, []);

  useEffect(() => {
    if (!isSuper) return;
    (async () => {
      try {
        const [sus, load] = await Promise.all([
          api.get("/audit/suspicious"),
          api.get("/dashboard/admin-load"),
        ]);
        setSuspicious(sus.data);
        setAdminLoad(load.data);
      } catch (e) { /* silent */ }
    })();
  }, [isSuper]);

  const isAdmin = user?.role === "admin" || user?.role === "super_admin";

  return (
    <AppShell>
      <div className="max-w-7xl mx-auto px-4 md:px-6 lg:px-12 py-6 md:py-12">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-3 mb-8">
          <div>
            <div className="text-[10px] tracking-[0.3em] uppercase text-[#C06C47] font-semibold mb-2">Dashboard</div>
            <h1 className="font-heading text-2xl md:text-4xl">Welcome back, {user?.name?.split(" ")[0]}</h1>
            <p className="text-[#5C6653] mt-2 text-sm md:text-base">Anuttara Township Phase 2 @ Kamareddy — inventory & booking control.</p>
          </div>
          <Link to="/map" data-testid="dashboard-goto-map" className="inline-flex items-center gap-2 bg-[#234B36] hover:bg-[#1A3A29] text-white px-5 py-2.5 text-sm w-fit">
            Open Layout Map <ArrowRight className="h-4 w-4" />
          </Link>
        </div>

        {/* KPIs */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4" data-testid="dashboard-kpis">
          <KPI label="Total Plots" value={stats?.total_plots ?? "—"} sub={`${stats?.total_lps ?? 0} LPs`} icon={Map} />
          <KPI
            label="Available"
            value={stats?.by_status?.fully_vacant ?? "—"}
            sub={`${stats?.by_status?.partially_vacant ?? 0} partial`}
            icon={TrendingUp}
            accent="#347355"
          />
          {isAdmin ? (
            <>
              <KPI label="Pending CPs" value={stats?.pending_users ?? "—"} sub="Awaiting approval" icon={Users} accent="#C06C47" />
              <KPI label="Pending Bookings" value={stats?.pending_bookings ?? "—"} sub={`${stats?.approved_bookings ?? 0} approved`} icon={BookOpenCheck} accent="#2C5E7A" />
            </>
          ) : (
            <>
              <KPI label="Sold" value={stats?.by_status?.sold ?? "—"} sub="Not available" icon={BookOpenCheck} accent="#858585" />
              <KPI label="Total Sq.Yd" value={stats?.total_sqyd ? Math.round(stats.total_sqyd).toLocaleString() : "—"} sub={`${stats?.vacant_sqyd ? Math.round(stats.vacant_sqyd).toLocaleString() : 0} vacant`} icon={TrendingUp} />
            </>
          )}
        </div>

        {/* Suspicious activity (Super Admin only) */}
        {isSuper && suspicious && (
          <SuspiciousPanel data={suspicious} />
        )}

        {/* Admin load heat-map (Super Admin only) */}
        {isSuper && adminLoad && (
          <AdminLoadPanel data={adminLoad} />
        )}

        {/* Legend */}
        <div className="mt-10 flex flex-wrap gap-4 text-sm">
          {Object.entries(STATUS_META).map(([k, m]) => (
            <div key={k} className="flex items-center gap-2">
              <span className="status-dot" style={{ background: m.bg }} />
              <span className="text-[#5C6653]">{m.label}</span>
            </div>
          ))}
        </div>

        {/* LP list */}
        <div className="mt-8">
          <div className="flex items-baseline justify-between mb-4">
            <h2 className="font-heading text-2xl">LP Layouts</h2>
            <div className="text-xs text-[#5C6653] font-mono-anu">{lps.length} approvals</div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4" data-testid="lp-grid">
            {lps.map((lp) => (
              <Link
                to={`/map?lp=${lp.id}`}
                key={lp.id}
                data-testid={`lp-card-${lp.lp_no}`}
                className="border border-[#E5E2DC] bg-white p-5 hover:border-[#234B36] transition-colors group"
              >
                <div className="flex items-start justify-between">
                  <div>
                    <div className="text-[10px] tracking-[0.3em] uppercase text-[#C06C47] font-semibold">{lp.phase_name}</div>
                    <div className="font-heading text-xl mt-1">{lp.lp_no}</div>
                    <div className="text-xs text-[#5C6653] mt-1">{lp.layout_acres ? `${lp.layout_acres} acres` : "—"}</div>
                  </div>
                  <ArrowRight className="h-5 w-5 text-[#5C6653] group-hover:text-[#234B36] group-hover:translate-x-1 transition-all" />
                </div>
                <div className="mt-4 grid grid-cols-4 gap-1 h-2">
                  {["fully_vacant", "partially_vacant", "booked", "sold"].map((s) => {
                    const c = lp.stats?.[s] || 0;
                    const total = lp.stats?.total || 1;
                    const w = (c / total) * 100;
                    return (
                      <div key={s} className="h-full" style={{ background: STATUS_META[s].bg, opacity: c === 0 ? 0.1 : 1, gridColumn: `span ${Math.max(1, Math.round(w / 25)) || 1}` }} />
                    );
                  })}
                </div>
                <div className="mt-3 flex justify-between text-xs text-[#5C6653]">
                  <span><span className="font-medium text-[#1A1F16]">{lp.stats?.total || 0}</span> plots</span>
                  <span className="text-[#347355]">{lp.stats?.fully_vacant || 0} vacant</span>
                  <span className="text-[#858585]">{lp.stats?.sold || 0} sold</span>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </div>
    </AppShell>
  );
}

function KPI({ label, value, sub, icon: Icon, accent }) {
  return (
    <div className="border border-[#E5E2DC] bg-white p-5 flex flex-col justify-between min-h-[110px]">
      <div className="flex items-center justify-between">
        <div className="text-[10px] tracking-[0.3em] uppercase text-[#5C6653] font-semibold">{label}</div>
        <Icon className="h-4 w-4" style={{ color: accent || "#234B36" }} />
      </div>
      <div>
        <div className="font-heading text-3xl mt-3" style={{ color: accent || "#1A1F16" }}>{value}</div>
        <div className="text-xs text-[#5C6653] mt-1">{sub}</div>
      </div>
    </div>
  );
}

function SuspiciousPanel({ data }) {
  const alerts = data?.alerts || [];
  const sev = { high: "#DC2626", medium: "#C06C47", low: "#5C6653" };
  const isClean = alerts.length === 0;
  return (
    <div className="mt-8 border rounded-sm" data-testid="suspicious-activity-panel"
      style={{ borderColor: isClean ? "#E5E2DC" : sev[alerts[0].severity] + "60", background: isClean ? "#FFFFFF" : "#FFF9F5" }}>
      <div className="flex items-start justify-between p-5 border-b" style={{ borderColor: isClean ? "#E5E2DC" : "#F0DFD4" }}>
        <div className="flex items-start gap-3">
          <div className="h-9 w-9 flex items-center justify-center rounded-sm" style={{ background: isClean ? "#34735515" : "#DC262615" }}>
            {isClean ? <ShieldAlert className="h-5 w-5 text-[#347355]" /> : <AlertTriangle className="h-5 w-5 text-[#DC2626]" />}
          </div>
          <div>
            <div className="text-[10px] tracking-[0.3em] uppercase text-[#C06C47] font-semibold">Security</div>
            <h2 className="font-heading text-xl mt-0.5">Suspicious activity</h2>
            <p className="text-xs text-[#5C6653] mt-1">
              {isClean
                ? "No risky patterns detected in the last 24 hours."
                : `${data.summary.total_alerts} alert(s) · ${data.summary.high_severity} high-severity · ${data.summary.failed_logins_24h} failed logins in 24h`}
            </p>
          </div>
        </div>
        <Link to="/admin/audit" data-testid="suspicious-view-log" className="text-xs text-[#234B36] underline underline-offset-4 whitespace-nowrap self-center">
          Full audit log →
        </Link>
      </div>

      {!isClean && (
        <div className="divide-y" style={{ borderColor: "#F0DFD4" }} data-testid="suspicious-alerts-list">
          {alerts.slice(0, 6).map((a, i) => (
            <div key={i} className="flex items-start gap-3 p-4" data-testid={`suspicious-alert-${a.type}`}>
              <span className="mt-1 h-2 w-2 rounded-full shrink-0" style={{ background: sev[a.severity] || "#5C6653" }} />
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-sm font-medium text-[#1A1F16]">{a.title}</span>
                  <span className="text-[10px] uppercase tracking-wider px-1.5 py-0.5 border font-semibold"
                    style={{ borderColor: sev[a.severity], color: sev[a.severity], background: sev[a.severity] + "12" }}>
                    {a.severity}
                  </span>
                  <span className="font-mono-anu text-xs text-[#5C6653]">{a.target}</span>
                </div>
                <div className="text-xs text-[#5C6653] mt-0.5">{a.description}</div>
              </div>
              <div className="text-right text-[10px] text-[#5C6653] whitespace-nowrap self-center">
                <div className="font-mono-anu text-sm text-[#1A1F16]">{a.count}</div>
                <div>{formatDate(a.last_seen)}</div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function AdminLoadPanel({ data }) {
  const admins = data?.admins || [];
  const sum = data?.summary || {};

  // Map load_score (0-100) to a tint colour on the warm scale.
  const bandColor = (score) => {
    if (score <= 15) return { bg: "#34735510", border: "#347355", label: "Cool" };
    if (score <= 40) return { bg: "#EAB30815", border: "#B08B00", label: "Warm" };
    if (score <= 65) return { bg: "#C06C4715", border: "#C06C47", label: "Hot" };
    return { bg: "#DC262612", border: "#DC2626", label: "Critical" };
  };

  return (
    <div className="mt-8 border border-[#E5E2DC] bg-white rounded-sm" data-testid="admin-load-panel">
      <div className="flex items-start justify-between p-5 border-b border-[#E5E2DC]">
        <div className="flex items-start gap-3">
          <div className="h-9 w-9 flex items-center justify-center rounded-sm bg-[#C06C4715]">
            <Flame className="h-5 w-5 text-[#C06C47]" />
          </div>
          <div>
            <div className="text-[10px] tracking-[0.3em] uppercase text-[#C06C47] font-semibold">Super Admin</div>
            <h2 className="font-heading text-xl mt-0.5">Admin load heat-map</h2>
            <p className="text-xs text-[#5C6653] mt-1">
              {admins.length} admin(s) · {sum.unassigned_channel_partners ?? 0} unassigned CP(s) · {sum.admins_with_no_cps ?? 0} admin(s) with no assigned CP
            </p>
          </div>
        </div>
        <Link to="/admin/assignments" data-testid="admin-load-goto-assign" className="text-xs text-[#234B36] underline underline-offset-4 whitespace-nowrap self-center">
          Manage assignments →
        </Link>
      </div>

      {admins.length === 0 ? (
        <div className="p-6 text-sm text-[#5C6653] text-center">No admins yet.</div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm min-w-[900px]" data-testid="admin-load-table">
            <thead className="bg-[#F0EFEA] text-left">
              <tr className="border-b border-[#E5E2DC]">
                <Th>Admin</Th>
                <Th>Assigned CPs</Th>
                <Th>Pending</Th>
                <Th>Approved</Th>
                <Th>Avg approval SLA</Th>
                <Th>Oldest pending</Th>
                <Th>Load</Th>
              </tr>
            </thead>
            <tbody>
              {admins.map((a) => {
                const band = bandColor(a.load_score);
                const oldest = a.oldest_pending_hours || 0;
                const oldestLabel = oldest === 0 ? "—" : oldest >= 24 ? `${(oldest / 24).toFixed(1)}d` : `${oldest.toFixed(1)}h`;
                const oldestTone = oldest >= 48 ? "text-[#DC2626]" : oldest >= 24 ? "text-[#C06C47]" : "text-[#5C6653]";
                return (
                  <tr key={a.admin_id} className="border-b border-[#E5E2DC]" style={{ background: band.bg }} data-testid={`admin-load-row-${a.admin_email}`}>
                    <TdRow>
                      <div className="font-medium">{a.admin_name}</div>
                      <div className="text-xs text-[#5C6653]">{a.admin_email}</div>
                      {a.admin_status === "suspended" && (
                        <div className="text-[10px] uppercase tracking-wider text-red-700 mt-0.5">Suspended admin — reassign CPs</div>
                      )}
                    </TdRow>
                    <TdRow><span className="font-mono-anu text-base">{a.assigned_cp_count}</span></TdRow>
                    <TdRow><span className={`font-mono-anu text-base ${a.pending_bookings > 0 ? "text-[#B08B00]" : "text-[#5C6653]"}`}>{a.pending_bookings}</span></TdRow>
                    <TdRow><span className="font-mono-anu text-base text-[#347355]">{a.approved_bookings}</span></TdRow>
                    <TdRow>
                      <div className="flex items-center gap-1 text-xs">
                        <Timer className="h-3 w-3 text-[#5C6653]" />
                        <span>{a.avg_approval_hours != null ? `${a.avg_approval_hours}h` : "—"}</span>
                      </div>
                    </TdRow>
                    <TdRow><span className={`text-xs ${oldestTone}`}>{oldestLabel}</span></TdRow>
                    <TdRow>
                      <div className="flex items-center gap-2">
                        <div className="flex-1 h-2 bg-[#F0EFEA] rounded-full overflow-hidden max-w-[110px]">
                          <div className="h-full" style={{ width: `${Math.min(a.load_score, 100)}%`, background: band.border }} />
                        </div>
                        <span className="text-[10px] uppercase tracking-wider font-semibold" style={{ color: band.border }}>{band.label}</span>
                      </div>
                    </TdRow>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function Th({ children }) { return <th className="px-4 py-3 text-[10px] tracking-[0.2em] uppercase text-[#5C6653] font-semibold whitespace-nowrap">{children}</th>; }
function TdRow({ children }) { return <td className="px-4 py-3 align-top">{children}</td>; }

